import { useEffect, useRef, useState } from 'react';
import { featuredRecommendations } from '@/mocks/home-extras';

export default function TestimonialsSection() {
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-charcoal py-20 md:py-28 lg:py-32 border-t border-white/5">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <div
            className={`text-center mb-14 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-4">
              Client and Team Feedback
            </p>
            <h2 className="text-white text-2xl md:text-3xl lg:text-display-sm font-bold leading-tight">
              What People Say
            </h2>
          </div>

          {/* Desktop: 3 cards side by side */}
          <div className="hidden md:grid grid-cols-3 gap-6">
            {featuredRecommendations.map((rec, idx) => (
              <div
                key={rec.id}
                className={`bg-dark-card rounded-xl p-7 border border-white/5 hover:border-gold/20 transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${idx * 150}ms` }}
              >
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                    <span className="text-gold font-bold text-sm">{rec.name.charAt(0)}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm">{rec.name}</p>
                    <p className="text-white/35 text-xs leading-snug">{rec.role}</p>
                  </div>
                </div>
                <p className="text-white/55 text-sm leading-relaxed">
                  &ldquo;{rec.endorsement}&rdquo;
                </p>
              </div>
            ))}
          </div>

          {/* Mobile: single card carousel */}
          <div className="md:hidden">
            <div
              className={`bg-dark-card rounded-xl p-7 border border-white/5 transition-all duration-500 ${
                visible ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                  <span className="text-gold font-bold text-sm">
                    {featuredRecommendations[active].name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">
                    {featuredRecommendations[active].name}
                  </p>
                  <p className="text-white/35 text-xs leading-snug">
                    {featuredRecommendations[active].role}
                  </p>
                </div>
              </div>
              <p className="text-white/55 text-sm leading-relaxed">
                &ldquo;{featuredRecommendations[active].endorsement}&rdquo;
              </p>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button
                type="button"
                onClick={() =>
                  setActive((prev) => (prev === 0 ? featuredRecommendations.length - 1 : prev - 1))
                }
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white/40 hover:border-gold hover:text-gold transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <div className="flex gap-2">
                {featuredRecommendations.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActive(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === active ? 'bg-gold' : 'bg-white/15'
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  setActive((prev) => (prev === featuredRecommendations.length - 1 ? 0 : prev + 1))
                }
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white/40 hover:border-gold hover:text-gold transition-colors"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}